import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Show, useEventListener } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../meta';
import { getFloatingNumber } from '../helpers';
import { useGutterResult, usePaddingResult, useSizeResult } from '../utils';
import { LogoComponent } from './LogoComponent';

const CAPTION_INLINE_MARGIN_VALUE = 16;
const CAPTION_BOTTOM_MARGIN_VALUE = 12;

export function Logo({ data, isDrag, width }) {
  const {
    editMode,
    captionEnabled,
    captionTextSize,
    captionTextColor,
    captionType,
    logoColor,
    logoBackgroundColor,
    minCaptionWidth,
    layout
  } = useSettings();

  const { file, link, caption, size, aspectRatio, maxSize } = data;
  const logoUrl = file?.data?.url;

  const logoInnerRef = useRef(null);
  const gutterResult = useGutterResult(width);
  const widthResult = useSizeResult(width, false, size);
  const logoPadding = usePaddingResult(width);
  const heightResult = useSizeResult(width, false, maxSize);
  const [logoHeight, setLogoHeight] = useState(0);

  const backgroundWrapperWidth = widthResult + 2 * logoPadding;
  const backgroundWrapperHeight = heightResult + 2 * logoPadding;

  const supportCustomColor = /\.svg$|\.png$/.test(logoUrl);
  const removeFilter = logoColor === 'custom' && !supportCustomColor;
  const hasBelowCaption =
    captionEnabled && captionType === 'below' && !!caption;
  const hasBackground =
    !!logoBackgroundColor && logoBackgroundColor !== 'transparent';

  useEffect(() => {
    if (!logoInnerRef.current) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      const height = getFloatingNumber(
        Math.min(widthResult, width) * aspectRatio
      );

      setLogoHeight(height);
    });

    observer.observe(logoInnerRef.current);

    return () => observer.disconnect();
  }, [widthResult, aspectRatio]);

  useEventListener('click', (e) => {
    if (isDrag) {
      e.preventDefault();
    }
  });

  const logoProps = useMemo(
    () => ({
      data,
      height: logoHeight,
      customColor: supportCustomColor
    }),
    [data, logoHeight, supportCustomColor]
  );

  const gridMarginBottom = (() => {
    if (layout !== 'grid') {
      return;
    }

    return logoBackgroundColor ? gutterResult : Math.trunc(gutterResult * 0.75);
  })();

  const captionMarginBottom = hasBackground
    ? Math.max(CAPTION_BOTTOM_MARGIN_VALUE, logoPadding)
    : undefined;
  const minCaptionWidthValue =
    hasBackground && hasBelowCaption ? minCaptionWidth : undefined;
  const innerMarginTop =
    captionEnabled && captionType === 'below' ? logoPadding : undefined;

  return (
    <Component
      _marginHorizontal={gutterResult / 2}
      _heightFull={layout === 'slider'}
      _backgroundWidth={backgroundWrapperWidth}
      _backgroundHeight={backgroundWrapperHeight}
      _minCaptionWidth={minCaptionWidthValue}
      _hasBackground={!!logoBackgroundColor}
      _backgroundColor={logoBackgroundColor ?? 'transparent'}
      _gridMarginBottom={gridMarginBottom}
      _justifyStart={captionEnabled && captionType === 'below'}
    >
      <Inner
        _width={widthResult}
        _height={heightResult}
        _marginTop={innerMarginTop}
        _removeFilter={removeFilter}
      >
        <LogoContent ref={logoInnerRef}>
          <Show
            when={link.rawValue}
            fallback={<LogoComponent {...logoProps} />}
          >
            <LogoLink
              href={link.rawValue}
              onDragStart={(e) => e.preventDefault()}
              target={editMode ? '_blank' : link.target}
              rel="nofollow noreferrer noopener"
            >
              <LogoComponent {...logoProps} />
            </LogoLink>
          </Show>
        </LogoContent>
      </Inner>

      <Show when={hasBelowCaption}>
        <Caption
          _color={captionTextColor}
          _minWidth={minCaptionWidth - CAPTION_INLINE_MARGIN_VALUE}
          _width={widthResult - CAPTION_INLINE_MARGIN_VALUE}
          _fontSize={captionTextSize?.fontSize}
          _fontWeight={captionTextSize?.fontWeight}
          _fontStyle={captionTextSize?.fontStyle}
          _marginBottom={captionMarginBottom}
        >
          {caption}
        </Caption>
      </Show>
    </Component>
  );
}

const Component = styled.div.attrs({
  className: `${meta.prefix}-logo-component`
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  max-width: 100%;
  width: fit-content;

  ${({
    _marginHorizontal,
    _heightFull,
    _backgroundWidth,
    _backgroundHeight,
    _minCaptionWidth,
    _hasBackground,
    _backgroundColor,
    _gridMarginBottom,
    _justifyStart
  }) => css`
    margin-left: ${_marginHorizontal}px;
    margin-right: ${_marginHorizontal}px;

    ${_minCaptionWidth && `min-width: ${_minCaptionWidth}px;`}
    ${_hasBackground && `background-color: ${_backgroundColor};`}
    ${_gridMarginBottom && `margin-bottom: ${_gridMarginBottom}px;`}
    ${_justifyStart && 'justify-content: flex-start;'}
    ${_heightFull && 'height: 100%;'}

    ${_hasBackground &&
    _backgroundColor !== 'transparent' &&
    css`
      width: ${_backgroundWidth}px;
      min-height: ${_backgroundHeight}px;
    `}
  `}
`;

const Inner = styled.div.attrs({
  className: `${meta.prefix}-logo-inner`
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  max-width: 100%;
  margin: 0 auto;

  ${({ _width, _height, _marginTop, _removeFilter }) => css`
    width: ${_width}px;
    height: ${_height}px;
    ${_marginTop && `margin-top: ${_marginTop}px;`}
    ${_removeFilter && 'filter: none;'}
  `}
`;

const LogoContent = styled.div.attrs({
  className: `${meta.prefix}-logo-innerInner`
})`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const LogoLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: static !important;
  text-decoration: none !important;

  &:hover {
    position: static !important;
    text-decoration: none !important;
  }
`;

const Caption = styled.div.attrs({
  className: `${meta.prefix}-logo-caption`
})`
  display: block;
  margin-top: 12px;
  margin-inline: 8px;
  text-align: center;
  line-height: 1.334;
  overflow-wrap: break-word;
  white-space: initial;

  ${({
    _color,
    _minWidth,
    _width,
    _fontSize,
    _fontWeight,
    _fontStyle,
    _marginBottom
  }) => css`
    color: ${_color};
    min-width: ${_minWidth}px;
    width: ${_width}px;

    ${_fontSize && `font-size: ${_fontSize}px;`}
    ${_fontWeight && `font-weight: ${_fontWeight};`}
    ${_fontStyle && `font-style: ${_fontStyle};`}
    ${_marginBottom && `margin-bottom: ${_marginBottom}px;`}
  `}
`;
